/**
 * State Classification Utilities
 * 
 * Functions to determine state status, colors, tooltips, and navigation URLs
 * based on state configuration data.
 */

import { STATE_CONFIG } from '../data/stateConfig.js';

/**
 * Get the status of a state
 * @param {string} stateCode - Two-letter state code (e.g., 'IL', 'FL')
 * @returns {string} Status: 'active', 'coming_soon', or 'no_data'
 */
export function getStateStatus(stateCode) {
  const state = STATE_CONFIG[stateCode];
  if (!state) return 'no_data';
  return state.status;
}

/**
 * Get the color for a state based on its status and hover state
 * @param {string} status - State status ('active', 'coming_soon', 'no_data')
 * @param {boolean} isHovered - Whether the state is currently hovered
 * @returns {string} Hex color code
 */
export function getStateColor(status, isHovered) {
  // Brand colors from PANTONE palette
  const COLORS = {
    active: '#319B42',      // PANTONE 7739C - Green (Available Today)
    activeHover: '#28a745', // Lighter green on hover
    comingSoon: '#004986',  // PANTONE 2186C - Blue (Coming Soon)
    noData: '#5B6770'       // PANTONE 431C - Grey (Not enough data)
  };
  
  if (isHovered && status === 'active') {
    return COLORS.activeHover;
  }
  if (status === 'active') {
    return COLORS.active;
  }
  if (status === 'coming_soon') {
    return COLORS.comingSoon;
  }
  return COLORS.noData; // no_data
}

/**
 * Get the tooltip message for a state
 * @param {string} stateCode - Two-letter state code
 * @returns {string} Tooltip message
 */
export function getTooltipMessage(stateCode) {
  const state = STATE_CONFIG[stateCode];
  
  if (!state) {
    return 'Not enough lotto data available';
  }
  
  switch (state.status) {
    case 'active':
      return state.name;
    case 'coming_soon':
      return 'Coming Soon! - Early 2026';
    case 'no_data':
      return 'Not enough lotto data available';
    default:
      return state.name;
  }
}

/**
 * Get the navigation URL for a state
 * @param {string} stateCode - Two-letter state code
 * @returns {string|null} Navigation URL or null if not navigable
 */
export function getNavigationURL(stateCode) {
  const state = STATE_CONFIG[stateCode];
  
  if (state && state.status === 'active' && state.slug) {
    // Use absolute URL to WordPress site for iframe compatibility
    return `https://www.scratchersparadise.com/${state.slug}/`;
  }
  
  return null;
}

/**
 * Check if a state is clickable (navigable)
 * @param {string} stateCode - Two-letter state code
 * @returns {boolean} True if state is clickable
 */
export function isStateClickable(stateCode) {
  return getNavigationURL(stateCode) !== null;
}
